import { useCallback, useEffect, useState } from 'react'
import Modal from 'react-modal'
import { AdminVenueEventCommentDetailDto } from '@ivy-monorepo/shared'
import { BaseModalProps } from 'src/types/modal'
import EventCommentActivityBox from '../EventCommentActivityBox'
import TextInput from '../ui/TextInput'
import ConfirmationModal from './ConfirmationModal'
import { adminService } from 'src/services/admin.service'

interface VenueEventCommentModalProps extends BaseModalProps {
  publicId: string | null
  onChanged?: () => void
  onDeleted?: (publicId: string) => void
}

const VenueEventCommentModal = ({ isOpen, onClose, publicId, onChanged, onDeleted }: VenueEventCommentModalProps) => {
  const [detail, setDetail] = useState<AdminVenueEventCommentDetailDto | null>(null)
  const [editContent, setEditContent] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'comment' | 'like'; id: string | number } | null>(null)

  const fetchDetail = useCallback(async () => {
    if (!publicId) {
      setDetail(null)
      return
    }

    try {
      setIsLoading(true)
      const response = await adminService.getEventCommentDetail(publicId)
      setDetail(response)
      setEditContent(response.content)
    } catch (err) {
      console.log('Failed to fetch event comment detail', err)
      setDetail(null)
    } finally {
      setIsLoading(false)
    }
  }, [publicId])

  useEffect(() => {
    if (!isOpen) {
      setDetail(null)
      setEditContent('')
      return
    }

    fetchDetail()
  }, [fetchDetail, isOpen])

  const handleSave = async () => {
    if (!detail) {
      return
    }

    try {
      setIsSaving(true)
      await adminService.updateEventComment(detail.publicId, editContent)
      await fetchDetail()
      onChanged?.()
    } catch (err) {
      console.log('Failed to update event comment', err)
    } finally {
      setIsSaving(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget || !detail) {
      return
    }

    try {
      if (deleteTarget.type === 'comment') {
        await adminService.deleteEventComment(detail.publicId)
        onDeleted?.(detail.publicId)
        onClose()
      } else {
        await adminService.deleteEventCommentLike(deleteTarget.id as number)
        setDetail(current =>
          current
            ? {
                ...current,
                likes: current.likes.filter(like => like.id !== deleteTarget.id),
              }
            : current
        )
        onChanged?.()
      }
    } catch (err) {
      console.log('Failed to delete event comment item', err)
    } finally {
      setDeleteTarget(null)
    }
  }

  const hasUnsavedChanges = detail ? editContent.trim() !== detail.content : false

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={onClose}
        ariaHideApp={false}
        className="modal-popup confirm-popup breadcrumb-detail-popup"
        overlayClassName="modal-overlay"
        shouldCloseOnEsc={true}
        shouldCloseOnOverlayClick={true}
        onAfterClose={() => {
          document.body.classList.remove('ReactModal__Body--open')
        }}
      >
        <div className="modal-content-wrapper">
          <div className="moderation-modal-header">
            <h2>Event comment</h2>
          </div>

          {isLoading && <div className="breadcrumb-detail-loading">Loading comment details...</div>}

          {!isLoading && detail && (
            <div className="breadcrumb-activity-sections breadcrumb-detail-sections">
              <EventCommentActivityBox
                title="Comment"
                variant="comment"
                content={detail.content}
                meta={[
                  {
                    label: 'Author',
                    value: `${detail.author.firstName} ${detail.author.lastName}`,
                  },
                  { label: 'Event', value: detail.event.name },
                  { label: 'Venue', value: detail.venue.name },
                  { label: 'Posted', value: detail.createdAt, type: 'datetime' },
                  { label: 'Updated', value: detail.updatedAt, type: 'datetime' },
                ]}
                onDelete={() => setDeleteTarget({ type: 'comment', id: detail.publicId })}
              />

              <div className="event-comment-edit-panel">
                <TextInput
                  label="Edit comment"
                  name="commentContent"
                  type="textArea"
                  required={true}
                  value={editContent}
                  onChange={setEditContent}
                />
                <button
                  type="button"
                  className="styled-button"
                  disabled={!hasUnsavedChanges || isSaving}
                  onClick={handleSave}
                >
                  {isSaving ? 'Saving...' : 'Save changes'}
                </button>
              </div>

              {detail.likes.length > 0 && (
                <div className="breadcrumb-replies-group">
                  <h3 className="breadcrumb-replies-heading">Likes ({detail.likes.length})</h3>
                  {detail.likes.map(like => (
                    <EventCommentActivityBox
                      key={like.id}
                      title={`${like.user.firstName} ${like.user.lastName}`}
                      variant="like"
                      meta={[{ label: 'Liked', value: like.createdAt, type: 'datetime' }]}
                      onDelete={() => setDeleteTarget({ type: 'like', id: like.id })}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="modal-footer-content">
            <button className="styled-button" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title={deleteTarget?.type === 'comment' ? 'Delete comment?' : 'Remove like?'}
      >
        <p>
          {deleteTarget?.type === 'comment'
            ? 'This will permanently delete the comment and its likes.'
            : 'This will remove this like from the comment.'}
        </p>
      </ConfirmationModal>
    </>
  )
}

export default VenueEventCommentModal
